import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { LOGO_URL } from "@/constants/brand";
import BrandLogo from "@/components/BrandLogo";
import { TEST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import {
  ScanLine, ShieldCheck, FileText, MessageCircle, Users, BarChart3,
  Sparkles, ArrowRight, Check, MapPin, Smartphone, Tractor, IndianRupee,
} from "lucide-react";

const FIELD_IMG =
  "https://images.unsplash.com/photo-1508175688576-0c076b47b5b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyZSUyMHRlY2glMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODI3MzUxNTJ8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: ScanLine, title: "QR Gate Passes", desc: "Replace paper slips. Generate, print and scan QR-coded entry & exit passes in seconds." },
  { icon: FileText, title: "Digital Receipts", desc: "Branded cash receipts as downloadable PDFs. Every rupee tracked, every helper accountable." },
  { icon: Users, title: "Multi-tenant Roles", desc: "Super Admin, Pashu Haat Owner, Helper, Seller, Purchaser — each with the right access." },
  { icon: BarChart3, title: "Daily & Monthly Reports", desc: "Helper-wise collection, animal-wise inflow, revenue snapshots — export ready." },
  { icon: MessageCircle, title: "WhatsApp & n8n Hooks", desc: "Notifications flow to sellers and purchasers via WhatsApp templates and n8n workflows." },
  { icon: ShieldCheck, title: "Audit-grade Security", desc: "Role-based access, tenant isolation, audit logs and encrypted sensitive data." },
];

const FALLBACK_PLANS = [
  { id: "starter", name: "Starter", price: 1999, highlight: false,
    features: ["1 Pashu Haat", "Up to 5 Helpers", "Full AnimalMela Features", "Standard Support"] },
  { id: "professional", name: "Professional", price: 2999, highlight: true,
    features: ["1 Pashu Haat", "Up to 10 Helpers", "Full AnimalMela Features", "Priority Support"] },
  { id: "enterprise", name: "Enterprise", price: 4999, highlight: false,
    features: ["2 Pashu Haats", "Up to 30 Helpers", "12,000 Animal Registrations / month", "Full AnimalMela Features", "Premium Support", "Multi-Haat Management"] },
];

export default function LandingPage() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [comparison, setComparison] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/plans`)
      .then((r) => {
        if (r.data.plans?.length) setPlans(r.data.plans);
        setComparison(r.data.comparison || []);
      })
      .catch(() => { /* keep static fallback */ });
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen">
      {/* NAV */}
      <header className="sticky top-0 z-40 am-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo size="desktopHeader" mobileSize="mobileHeader" />
            <span className="font-display font-extrabold text-lg">AnimalMela</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold">
            <a href="#features" className="hover:text-primary transition-colors">Features</a>
            <a href="#how" className="hover:text-primary transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-primary transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid={TEST.navLogin} className="text-sm font-semibold px-4 py-2 rounded-full hover:bg-muted">
              Sign in
            </Link>
            <Link
              to="/login"
              data-testid={TEST.navGetStarted}
              className="am-btn-primary text-sm py-2.5 hidden sm:inline-flex"
            >
              Get started <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30">
          <img src={FIELD_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/95 to-background" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 am-reveal">
            <span className="am-chip mb-6">
              <Sparkles className="w-3.5 h-3.5" /> India&apos;s Digital OS for Pashu Haats
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Digitize your <span className="text-primary">Pashu Haat.</span>
              <br />
              Retire the paper slips.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              AnimalMela is a multi-tenant SaaS that turns livestock-market chaos into clean digital records — QR
              gate passes, cash receipts, WhatsApp alerts and helper-wise reports, all in one operating system.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" data-testid={TEST.heroCta} className="am-btn-primary inline-flex items-center">
                Start free trial <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/login" data-testid={TEST.heroLogin} className="am-btn-secondary inline-flex items-center">
                Sign in with OTP
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "10,000+", v: "Haats supported" },
                { k: "1M+", v: "Animal records" },
                { k: "₹0", v: "Paper slips" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-2xl font-extrabold text-primary">{s.k}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 am-reveal" style={{ animationDelay: "0.15s" }}>
            <div className="space-y-5">
              <div className="am-card p-5 bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-accent grid place-items-center"><ScanLine className="w-5 h-5 text-primary" /></div>
                  <div>
                    <div className="font-semibold text-sm">Gate Pass GP-2026-00128</div>
                    <div className="text-xs text-muted-foreground">Buffalo · 480 kg · Vehicle UP-25-CD-1109</div>
                  </div>
                </div>
              </div>
              <div className="am-card p-5 bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-accent grid place-items-center"><BarChart3 className="w-5 h-5 text-primary" /></div>
                  <div>
                    <div className="font-semibold text-sm">Today&apos;s collection</div>
                    <div className="text-xs text-muted-foreground font-mono">₹ 42,560 · 87 receipts</div>
                  </div>
                </div>
              </div>
              <div className="am-card p-5 bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-accent grid place-items-center"><MessageCircle className="w-5 h-5 text-primary" /></div>
                  <div>
                    <div className="font-semibold text-sm">WhatsApp receipt sent</div>
                    <div className="text-xs text-muted-foreground">Seller · Ramesh Yadav · 2 min ago</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-sm font-bold tracking-widest uppercase text-primary">Built for the ground</span>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-3">Everything a Pashu Haat needs to run digitally.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">From the cashier&apos;s stall to the gate scanner, AnimalMela threads the entire haat onto a single, audit-ready system.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="am-card p-7 am-reveal" style={{ animationDelay: `${i * 0.06}s` }}>
                  <div className="w-12 h-12 rounded-2xl bg-accent grid place-items-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-bold">{f.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-bold tracking-widest uppercase text-primary">How it works</span>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-3">Three roles. One smooth workflow.</h2>
            <ol className="mt-8 space-y-6">
              {[
                { n: "01", t: "Super Admin onboards a Pashu Haat Owner", d: "Pick a plan, set helper limit, activate the tenant. The Owner gets OTP login instantly.", icon: ShieldCheck },
                { n: "02", t: "Owner configures fees & adds Helpers", d: "Set per-animal fees, gate-pass charges, add field helpers within the plan limit.", icon: Tractor },
                { n: "03", t: "Helpers register sellers, animals & collect cash", d: "Animal cards, QR gate passes and PDF receipts — generated on a phone in seconds.", icon: Smartphone },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.n} className="flex gap-5">
                    <div className="font-mono text-3xl font-bold text-primary/30 w-14 flex-shrink-0">{s.n}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-primary" />
                        <h3 className="font-display font-bold text-lg">{s.t}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.d}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <span className="text-sm font-bold tracking-widest uppercase text-primary">Pricing</span>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-3">Flat monthly. No per-animal trickery.</h2>
            <p className="mt-4 text-muted-foreground">Pick a plan that matches your helper count. Cancel anytime.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((p) => (
              <div
                key={p.id}
                data-testid={TEST.pricingPlan(p.id)}
                className={`am-card p-8 flex flex-col ${p.highlight ? "ring-2 ring-primary border-primary" : ""}`}
              >
                {p.highlight && (
                  <div className="am-chip self-start mb-4">
                    <Sparkles className="w-3 h-3" /> Most popular
                  </div>
                )}
                <div className="font-display font-bold text-2xl">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <IndianRupee className="w-5 h-5 text-primary mb-1" />
                  <span className="font-display text-4xl font-extrabold">{p.price.toLocaleString("en-IN")}</span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((ft) => (
                    <li key={ft} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{ft}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/login"
                  data-testid={TEST.pricingCta(p.id)}
                  className={`mt-8 ${p.highlight ? "am-btn-primary" : "am-btn-secondary"} text-center`}
                >
                  Start {p.name}
                </Link>
              </div>
            ))}
          </div>

          {/* Comparison table */}
          {comparison.length > 0 && (
            <div className="mt-14 max-w-5xl mx-auto" data-testid="pricing-comparison">
              <h3 className="font-display font-bold text-2xl text-center mb-6">Compare plans</h3>
              <div className="am-card p-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="px-4 py-4 font-semibold">Feature</th>
                      <th className="px-4 py-4 text-center font-display font-bold">Starter</th>
                      <th className="px-4 py-4 text-center font-display font-bold bg-accent/40 rounded-t-2xl">
                        Professional
                        <div className="text-[10px] uppercase tracking-widest text-primary mt-0.5">Most popular</div>
                      </th>
                      <th className="px-4 py-4 text-center font-display font-bold">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.map((row) => (
                      <tr key={row.feature} className="border-b last:border-0">
                        <td className="px-4 py-3.5 font-medium">{row.feature}</td>
                        {["starter", "professional", "enterprise"].map((pid) => {
                          const v = row[pid];
                          const isBool = typeof v === "boolean";
                          return (
                            <td key={pid} className={`px-4 py-3.5 text-center ${pid === "professional" ? "bg-accent/20" : ""}`}>
                              {isBool ? (
                                v ? <Check className="w-4 h-4 text-primary mx-auto" /> : <span className="text-muted-foreground/40">—</span>
                              ) : (
                                <span className="text-sm">{v}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="am-card p-10 lg:p-14 bg-primary text-primary-foreground border-primary relative overflow-hidden">
            <div className="relative z-10 grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="font-display text-3xl sm:text-4xl font-extrabold">Bring your haat online this week.</h3>
                <p className="mt-3 text-primary-foreground/80 leading-relaxed">Onboard in 10 minutes. Train helpers in 30. The receipt book retires the same day.</p>
              </div>
              <div className="md:text-right">
                <Link to="/login" className="inline-flex items-center bg-background text-foreground rounded-full px-6 py-3 font-semibold hover:bg-accent transition-all">
                  Sign in <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GOVERNMENT INITIATIVES — we support */}
      <section className="pb-20" data-testid="initiatives-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                img: "/initiatives/digital-india.svg",
                alt: "Digital India",
                text: "We support the Digital India initiative for digitizing Government activities and helping improve digital literacy.",
                testId: "initiative-digital-india",
              },
              {
                img: "/initiatives/startup-india.png",
                alt: "Startup India",
                text: "We support the Startup India initiative that aims to accelerate entrepreneurship in the country and create startups.",
                testId: "initiative-startup-india",
              },
              {
                img: "/initiatives/make-in-india.png",
                alt: "Make in India",
                text: "We support the Make in India initiative that encourages and facilitates foreign investment into the country.",
                testId: "initiative-make-in-india",
              },
            ].map((c) => (
              <div key={c.alt} data-testid={c.testId} className="am-card p-8 bg-card flex flex-col items-center text-center gap-6">
                <p className="text-sm text-muted-foreground leading-relaxed">{c.text}</p>
                <img src={c.img} alt={c.alt} loading="lazy" className="h-20 w-auto max-w-[220px] object-contain mt-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-4 items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2.5">
            <BrandLogo size="footer" alt="AnimalMela" />
            <span>© {new Date().getFullYear()} AnimalMela. Made for Bharat&apos;s livestock economy.</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/privacy-policy" className="underline underline-offset-4 hover:text-foreground" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <span aria-hidden="true">|</span>
            <Link to="/terms-and-conditions" className="underline underline-offset-4 hover:text-foreground" data-testid="footer-terms-link">
              Terms &amp; Conditions
            </Link>
            <span aria-hidden="true">|</span>
            <Link to="/refund-cancellation-policy" className="underline underline-offset-4 hover:text-foreground" data-testid="footer-refund-link">
              Refund &amp; Cancellation Policy
            </Link>
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> India</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
